import React, { useState, useEffect, useMemo } from 'react';
import './Loader.css';

const Loader = ({ size = 'medium', text = 'Loading...', fullPage = false, isDataReady = true, onComplete }) => {
    const [progress, setProgress] = useState(0);
    const [currentBlock, setCurrentBlock] = useState(0);
    const totalBlocks = 5;
    const cycleDuration = 2000;

    const blockPatterns = useMemo(() => [
        `<rect x="5" y="5" width="70" height="30" fill="none" stroke="#2196F3" stroke-width="2.5" rx="3" stroke-dasharray="0"/>
         <line x1="15" y1="12" x2="65" y2="12" stroke="#64B5F6" stroke-width="1.5" stroke-dasharray="4,3"/>
         <line x1="15" y1="20" x2="55" y2="20" stroke="#64B5F6" stroke-width="1.5" stroke-dasharray="4,3"/>
         <line x1="15" y1="28" x2="60" y2="28" stroke="#64B5F6" stroke-width="1.5" stroke-dasharray="4,3"/>`,
        `<rect x="5" y="5" width="70" height="30" fill="none" stroke="#1976D2" stroke-width="2.5" rx="3"/>
         <circle cx="20" cy="20" r="5" fill="none" stroke="#42A5F5" stroke-width="1.5"/>
         <circle cx="40" cy="20" r="5" fill="none" stroke="#42A5F5" stroke-width="1.5"/>
         <circle cx="60" cy="20" r="5" fill="none" stroke="#42A5F5" stroke-width="1.5"/>`,
        `<rect x="5" y="5" width="70" height="30" fill="none" stroke="#0D47A1" stroke-width="2.5" rx="3"/>
         <path d="M15,12 L25,20 L15,28" fill="none" stroke="#64B5F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
         <path d="M35,12 L45,20 L35,28" fill="none" stroke="#64B5F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
         <path d="M55,12 L65,20 L55,28" fill="none" stroke="#64B5F6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>`,
        `<rect x="5" y="5" width="70" height="30" fill="none" stroke="#1565C0" stroke-width="2.5" rx="3"/>
         <rect x="12" y="10" width="15" height="20" fill="none" stroke="#90CAF9" stroke-width="1.5" rx="2"/>
         <rect x="32" y="10" width="15" height="20" fill="none" stroke="#90CAF9" stroke-width="1.5" rx="2"/>
         <rect x="52" y="10" width="15" height="20" fill="none" stroke="#90CAF9" stroke-width="1.5" rx="2"/>`,
        `<rect x="5" y="5" width="70" height="30" fill="none" stroke="#2196F3" stroke-width="2.5" rx="3"/>
         <line x1="10" y1="10" x2="70" y2="10" stroke="#BBDEFB" stroke-width="1.5"/>
         <line x1="10" y1="20" x2="70" y2="20" stroke="#BBDEFB" stroke-width="1.5"/>
         <line x1="10" y1="30" x2="70" y2="30" stroke="#BBDEFB" stroke-width="1.5"/>
         <line x1="25" y1="5" x2="25" y2="35" stroke="#BBDEFB" stroke-width="1.5"/>
         <line x1="55" y1="5" x2="55" y2="35" stroke="#BBDEFB" stroke-width="1.5"/>`
    ], []);

    const startTimeRef = React.useRef(Date.now());
    const hasCalledCompleteRef = React.useRef(false);
    const requestRef = React.useRef();

    useEffect(() => {
        if (!fullPage) return;

        const animate = () => {
            const now = Date.now();
            const elapsed = now - startTimeRef.current;

            // Linear building animation (0 to 100%)
            const progressValue = Math.min((elapsed / cycleDuration) * 100, 100);
            setProgress(progressValue);

            const targetBlock = Math.min(Math.floor((progressValue / 100) * (totalBlocks + 1)), totalBlocks);
            setCurrentBlock(prev => prev !== targetBlock ? targetBlock : prev);

            // Completion Logic
            if (progressValue >= 100 && isDataReady && !hasCalledCompleteRef.current) {
                hasCalledCompleteRef.current = true;
                if (onComplete) {
                    setTimeout(() => {
                        onComplete();
                    }, 800);
                }
                return; // Stop the loop
            }

            requestRef.current = requestAnimationFrame(animate);
        };

        requestRef.current = requestAnimationFrame(animate);
        return () => {
            if (requestRef.current) cancelAnimationFrame(requestRef.current);
        };
    }, [fullPage, isDataReady, onComplete]);

    if (!fullPage) {
        return (
            <div className={`loader-container ${size}`}>
                <div className="circular-loader"></div>
                {text && <span className="loader-text-small">{text}</span>}
            </div>
        );
    }

    return (
        <div className={`loader-container ${size} full-page`}>
            <svg className="sketch-filter">
                <defs>
                    <filter id="sketch">
                        <feTurbulence type="turbulence" baseFrequency="0.02" numOctaves="3" result="noise" />
                        <feDisplacementMap in="SourceGraphic" in2="noise" scale="1" />
                    </filter>
                </defs>
            </svg>

            <div className="construction-scene">
                {/* Sun */}
                <div className="sun">
                    <svg width="50" height="50" viewBox="0 0 50 50">
                        <circle cx="25" cy="25" r="12" fill="#FFD54F" stroke="#FFA726" stroke-width="2" />
                        <g stroke="#FFA726" stroke-width="2" stroke-linecap="round">
                            <line x1="25" y1="5" x2="25" y2="10" />
                            <line x1="25" y1="40" x2="25" y2="45" />
                            <line x1="5" y1="25" x2="10" y2="25" />
                            <line x1="40" y1="25" x2="45" y2="25" />
                            <line x1="10" y1="10" x2="14" y2="14" />
                            <line x1="36" y1="36" x2="40" y2="40" />
                            <line x1="10" y1="40" x2="14" y2="36" />
                            <line x1="36" y1="14" x2="40" y2="10" />
                        </g>
                    </svg>
                </div>

                {/* Clouds */}
                <div className="cloud cloud-1">
                    <svg width="70" height="40" viewBox="0 0 70 40">
                        <path d="M15,30 Q5,30 5,22 Q5,14 15,14 Q15,5 30,5 Q45,5 50,15 Q60,10 65,20 Q70,30 55,30 Z"
                            fill="none" stroke="#90CAF9" stroke-width="2" stroke-dasharray="4,2" />
                    </svg>
                </div>
                <div className="cloud cloud-2">
                    <svg width="50" height="30" viewBox="0 0 50 30">
                        <path d="M10,25 Q2,25 2,18 Q2,11 12,11 Q12,3 25,3 Q38,3 40,12 Q50,10 48,20 Q46,28 35,25 Z"
                            fill="none" stroke="#90CAF9" stroke-width="2" stroke-dasharray="3,2" />
                    </svg>
                </div>

                {/* Birds */}
                <div className="bird bird-1">
                    <svg width="20" height="15" viewBox="0 0 20 15">
                        <path d="M2,10 Q5,5 10,8 Q15,5 18,10" fill="none" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                    </svg>
                </div>
                <div className="bird bird-2">
                    <svg width="15" height="12" viewBox="0 0 15 12">
                        <path d="M1,8 Q4,3 7,6 Q10,3 14,8" fill="none" stroke="#42A5F5" stroke-width="2" stroke-linecap="round" />
                    </svg>
                </div>

                {/* Worker Left */}
                <div className="worker worker-left">
                    <svg viewBox="0 0 50 70">
                        <ellipse cx="25" cy="12" rx="12" ry="6" fill="#FFA726" stroke="#E65100" stroke-width="2" />
                        <rect x="13" y="10" width="24" height="4" fill="#FFA726" />
                        <circle cx="25" cy="18" r="8" fill="none" stroke="#1976D2" stroke-width="2" />
                        <circle cx="22" cy="17" r="1.5" fill="#1976D2" />
                        <circle cx="28" cy="17" r="1.5" fill="#1976D2" />
                        <path d="M22,21 Q25,24 28,21" fill="none" stroke="#1976D2" stroke-width="1.5" stroke-linecap="round" />
                        <line x1="25" y1="26" x2="25" y2="45" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                        <line x1="25" y1="32" x2="40" y2="28" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                        <line x1="25" y1="32" x2="40" y2="38" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                        <rect x="38" y="26" width="12" height="14" fill="none" stroke="#42A5F5" stroke-width="2" rx="2" />
                        <line x1="41" y1="30" x2="47" y2="30" stroke="#42A5F5" stroke-width="1.5" />
                        <line x1="41" y1="36" x2="47" y2="36" stroke="#42A5F5" stroke-width="1.5" />
                        <line x1="25" y1="45" x2="18" y2="65" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                        <line x1="25" y1="45" x2="32" y2="65" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                    </svg>
                </div>

                {/* Worker Right */}
                <div className="worker worker-right">
                    <svg viewBox="0 0 50 70">
                        <ellipse cx="25" cy="12" rx="12" ry="6" fill="#FFD54F" stroke="#FFA726" stroke-width="2" />
                        <rect x="13" y="10" width="24" height="4" fill="#FFD54F" />
                        <circle cx="25" cy="18" r="8" fill="none" stroke="#1976D2" stroke-width="2" />
                        <circle cx="22" cy="17" r="1.5" fill="#1976D2" />
                        <circle cx="28" cy="17" r="1.5" fill="#1976D2" />
                        <path d="M22,21 Q25,24 28,21" fill="none" stroke="#1976D2" stroke-width="1.5" stroke-linecap="round" />
                        <line x1="25" y1="26" x2="25" y2="45" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                        <line x1="25" y1="32" x2="10" y2="28" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                        <g transform="translate(5, 20)" style={{ animation: 'hammerSwing 0.5s ease-in-out infinite' }}>
                            <line x1="5" y1="8" x2="5" y2="20" stroke="#8D6E63" stroke-width="3" stroke-linecap="round" />
                            <rect x="0" y="2" width="10" height="8" fill="#78909C" stroke="#546E7A" stroke-width="1" rx="1" />
                        </g>
                        <line x1="25" y1="35" x2="35" y2="42" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                        <line x1="25" y1="45" x2="18" y2="65" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                        <line x1="25" y1="45" x2="32" y2="65" stroke="#1976D2" stroke-width="2" stroke-linecap="round" />
                    </svg>
                </div>

                {/* Ladder */}
                <div className="ladder">
                    <svg width="25" height="100" viewBox="0 0 25 100">
                        <line x1="5" y1="0" x2="5" y2="100" stroke="#8D6E63" stroke-width="3" stroke-linecap="round" />
                        <line x1="20" y1="0" x2="20" y2="100" stroke="#8D6E63" stroke-width="3" stroke-linecap="round" />
                        <line x1="5" y1="15" x2="20" y2="15" stroke="#A1887F" stroke-width="2" />
                        <line x1="5" y1="35" x2="20" y2="35" stroke="#A1887F" stroke-width="2" />
                        <line x1="5" y1="55" x2="20" y2="55" stroke="#A1887F" stroke-width="2" />
                        <line x1="5" y1="75" x2="20" y2="75" stroke="#A1887F" stroke-width="2" />
                        <line x1="5" y1="95" x2="20" y2="95" stroke="#A1887F" stroke-width="2" />
                    </svg>
                </div>

                {/* Tools */}
                <div className="tools">
                    <svg width="60" height="30" viewBox="0 0 60 30">
                        <path d="M5,25 L20,10 M17,13 L23,7 M14,16 L20,10" stroke="#78909C" stroke-width="2" stroke-linecap="round" />
                        <line x1="30" y1="28" x2="45" y2="8" stroke="#FFA726" stroke-width="3" stroke-linecap="round" />
                        <line x1="45" y1="8" x2="50" y2="3" stroke="#78909C" stroke-width="2" stroke-linecap="round" />
                    </svg>
                </div>

                {/* Building Blocks */}
                <div className="building-blocks">
                    {[...Array(currentBlock)].map((_, i) => (
                        <div key={i} className="block" style={{ animation: 'blockDrop 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards' }}>
                            <svg viewBox="0 0 80 40" dangerouslySetInnerHTML={{ __html: blockPatterns[i % blockPatterns.length] }} />
                        </div>
                    ))}
                </div>

                {/* Ground */}
                <div className="ground-line"></div>
            </div>

            {/* Progress Container */}
            <div className="progress-container">
                <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${progress}%` }}></div>
                </div>
                <div className="loading-text">
                    {text.split('').map((char, i) => (
                        <span key={i} style={{ animationDelay: `${i * 0.05}s` }}>{char}</span>
                    ))}
                </div>
                <div className="percentage">{Math.round(progress)}%</div>
            </div>
        </div>
    );
};

export default Loader;
